import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Crown, Check, X, Shield, CreditCard, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const SUBSCRIPTION_PLANS = [
  {
    id: 'weekly',
    name: '1 Semaine',
    pricePerWeek: 20,
    pricePerMonth: null,
    totalPrice: 20,
    duration: '7 jours',
    discount: null,
    popular: false
  },
  {
    id: 'monthly',
    name: '1 Mois',
    pricePerWeek: 11.54,
    pricePerMonth: 50,
    totalPrice: 50,
    duration: '1 mois',
    discount: 42,
    popular: false
  },
  {
    id: 'quarterly',
    name: '3 Mois',
    pricePerWeek: 9.23,
    pricePerMonth: 40,
    totalPrice: 120,
    duration: '3 mois',
    discount: 54,
    popular: true
  },
  {
    id: 'semiannual',
    name: '6 Mois',
    pricePerWeek: 6.92,
    pricePerMonth: 30,
    totalPrice: 180,
    duration: '6 mois',
    discount: 65,
    popular: false
  },
  {
    id: 'annual',
    name: '1 An',
    pricePerWeek: 4.62,
    pricePerMonth: 20,
    totalPrice: 240,
    duration: '12 mois',
    discount: 77,
    popular: false,
    bestValue: true
  }
];

const FEATURES = [
  'Accès à toutes les photos des profils',
  'Messagerie illimitée',
  'Chat en temps réel',
  'Recherche avancée avec filtres',
  'Photos privées (avec autorisation)',
  'Validation des nouveaux membres',
  'Support prioritaire'
];

export default function SubscriptionPopup({ isOpen, onClose, triggerSource = 'profile' }) {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('quarterly');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await api.post('/subscriptions/create', {
        plan: selectedPlan,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du paiement';
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-dark-paper border border-gold/20 p-0">
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-br from-dark-paper via-dark to-dark-paper p-8 border-b border-gold/20">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-4">
              <Crown className="w-8 h-8 text-gold" />
            </div>
            <h2 className="font-serif text-3xl gold-gradient-text mb-2">
              Abonnez-vous
            </h2>
            <p className="text-gray-400 max-w-md mx-auto">
              Accédez à toutes les fonctionnalités de DiscreetLux : photos, messagerie, chat en direct...
            </p>
          </div>
        </div>

        <div className="p-8">
          {/* Plans Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            {SUBSCRIPTION_PLANS.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                  selectedPlan === plan.id
                    ? 'border-gold bg-gold/10'
                    : 'border-white/10 hover:border-gold/50 bg-dark-subtle'
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gold text-dark text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      POPULAIRE
                    </span>
                  </div>
                )}
                
                {/* Best Value Badge */}
                {plan.bestValue && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> MEILLEUR
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h3 className={`font-semibold mb-2 ${selectedPlan === plan.id ? 'text-gold' : 'text-white'}`}>
                    {plan.name}
                  </h3>
                  
                  <div className="mb-2">
                    <span className={`text-2xl font-bold ${selectedPlan === plan.id ? 'text-gold' : 'text-white'}`}>
                      {plan.pricePerWeek.toFixed(2).replace('.', ',')}€
                    </span>
                    <span className="text-gray-500 text-sm">/sem</span>
                  </div>

                  {plan.discount && (
                    <div className="inline-block bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded">
                      -{plan.discount}%
                    </div>
                  )}

                  {plan.pricePerMonth && (
                    <div className="mt-2 text-xs text-gray-500">
                      {plan.pricePerMonth}€/mois
                    </div>
                  )}
                </div>

                {/* Selection indicator */}
                {selectedPlan === plan.id && (
                  <div className="absolute top-2 right-2">
                    <div className="w-5 h-5 rounded-full bg-gold flex items-center justify-center">
                      <Check className="w-3 h-3 text-dark" />
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Features List */}
          <div className="glass-card p-6 mb-6">
            <h3 className="font-semibold text-gold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Vos avantages
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FEATURES.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-gold" />
                  </div>
                  <span className="text-gray-300 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* NextDev info - above button */}
          <p className="text-center text-gray-400 text-sm mb-4">
            Le nom qui apparaîtra sur votre relevé bancaire sera : <strong className="text-gold">NextDev</strong>
          </p>

          {/* Subscribe Button */}
          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full btn-gold h-14 text-lg font-semibold flex items-center justify-center gap-3"
            data-testid="subscribe-btn"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                S'abonner maintenant
              </>
            )}
          </Button>

          {/* Payment Info */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-gray-500 text-xs">
              Paiement sécurisé par Stripe
            </p>
            <div className="flex items-center justify-center gap-4 mt-3">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/2560px-Stripe_Logo%2C_revised_2016.svg.png" alt="Stripe" className="h-6 opacity-50" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
